import { DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable } from 'rxjs';
import { TableColumn } from '../../../../@vex/interfaces/table-column.interface';
import { RealEstate } from '@eadsysteme/ewsrealestateclient-v2';
import { Billing, BillingCustomCService, RealEstates } from '@eadsysteme/billingclient-v1';
import { RealEstateTableEntity } from '../models/real-estate-table.model';
import { RealEstateViewStrategy, ViewType } from './real-estate-view.strategy';
import { SessionService } from '../../../services/session.service';
import { BillingSelectDialogComponent } from '../billing-select-dialog/billing-select-dialog.component';
import { SnackbarService } from '../../../services/snackbar.service';
import { translate } from '@jsverse/transloco';
import { PeriodService } from '../../billings/billing/period.service';

@Injectable()
export class BillingViewStrategy implements RealEstateViewStrategy {
	readonly viewType = ViewType.BILLING;
	readonly menuName = 'BILLING';

	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly dialog = inject(MatDialog);

	private readonly billingCustomCService = inject(BillingCustomCService);
	private readonly sessionService = inject(SessionService);
	private readonly snackbarService = inject(SnackbarService);
	private readonly selectedPeriodService = inject(PeriodService);

	private filterPortalActiveBillings(billings: Billing[] | null | undefined): Billing[] {
		return (billings ?? []).filter((billing) => billing.portalActive !== false);
	}

	getColumns(baseColumns: TableColumn<RealEstate>[], translate: (key: string) => string): TableColumn<RealEstate>[] {
		return [
			...baseColumns,
			{
				label: translate('billing.billings'),
				property: 'billings',
				type: 'text',
				visible: true,
			},
		];
	}

	enrichRowData(dataSource: MatTableDataSource<RealEstateTableEntity>, destroyRef: DestroyRef): void {
		const searchedRealEstates: RealEstates = {
			realEstates: dataSource.data.map((re) => {
				return {
					realEstateNumber: re.realEstateNumber,
					area: re.realEstateArea,
					realEstateId: re.realEstateId,
				};
			}),
		};

		this.billingCustomCService
			.getBillingsByRealEstates(searchedRealEstates)
			.pipe(takeUntilDestroyed(destroyRef))
			.subscribe({
				next: (billings: Billing[]) => {
					const activeBillings = this.filterPortalActiveBillings(billings);
					const billingsByRealEstateId = new Map<string, Billing[]>();

					for (const billing of activeBillings) {
						const existingBillings = billingsByRealEstateId.get(billing.realEstateId) || [];
						existingBillings.push(billing);
						billingsByRealEstateId.set(billing.realEstateId, existingBillings);
					}

					for (const row of dataSource.data) {
						row.billings = billingsByRealEstateId.get(row.realEstateId) || [];
					}
				},
				error: (err) => {
					this.snackbarService.error(translate('errors.loadingBillings'));
				},
			});
	}

	onRowClick(row: RealEstateTableEntity, router: Router): void {
		if (row.billings?.length === 1) {
			router.navigate(['billing-units/' + row.billings[0].exportId]);
		} else if (row.billings?.length > 1) {
			this.openBillingSelectDialog(row);
		}
	}

	onBillingChipClick(billing: Billing): void {
		this.router.navigate(['/billing-units/' + billing.exportId]);
	}

	openBillingSelectDialog(row: RealEstateTableEntity): void {
		this.dialog.open(BillingSelectDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			width: '400px',
			data: {
				billings: row.billings,
			},
		});
	}

	toBilling(row: RealEstateTableEntity) {
		this.selectedPeriodService.selectedPeriod.set(undefined);
		this.router.navigate(['/billing-units/' + row.billings?.[0]?.exportId]);
	}

	enrichSingleEntity(entity: RealEstateTableEntity): Observable<RealEstateTableEntity> {
		return this.billingCustomCService.getBillingsByRealEstate(this.sessionService.effectiveArea, entity.realEstateId).pipe(
			map((billings: Billing[]) => {
				entity.billings = this.filterPortalActiveBillings(billings).filter((b) => b.realEstateId === entity.realEstateId);
				return entity;
			})
		);
	}

	handlePinnedNavigation(entity: RealEstateTableEntity, router: Router): void {
		this.onRowClick(entity, router);
	}
}
